import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SET_RECAPTCHA } from '@/store/slices/appSlice';
import Apm from '@/utils/Apm';
import Nullable from '@/types/utils/Nullable';

declare global {
  interface Window {
    grecaptcha?: any;
  }
}

interface UseRecaptchaReturn {
  validate: (action?: string) => Promise<string>;
  reset: () => void;
  token: Nullable<string>;
  isLoading: boolean;
  error: Nullable<string>;
}

const TOKEN_EXPIRATION_TIME = 2 * 60 * 1000; // 2 minutos
const VALIDATION_COOLDOWN = 1000; // 1 segundo entre validações
const GRECAPTCHA_LOAD_TIMEOUT = 10000; // 10 segundos para carregar

// Função utilitária fora do hook
const getExecuteMethod = (grecaptcha: any): Function => {
  const version = process.env.NEXT_PUBLIC_RECAPTCHA_VERSION || 'enterprise';

  if (version === 'enterprise' && grecaptcha.enterprise) {
    return grecaptcha.enterprise.execute;
  } else if (grecaptcha.execute) {
    return grecaptcha.execute;
  }

  throw new Error('Método de execução do reCAPTCHA não disponível');
};

const useRecaptcha = (): UseRecaptchaReturn => {
  const dispatch = useDispatch();
  const [token, setToken] = useState<Nullable<string>>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Nullable<string>>(null);

  const tokenTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastTokenTimeRef = useRef<number>(0);
  const isValidatingRef = useRef<boolean>(false);

  // Aguardar o carregamento do grecaptcha
  const waitForGrecaptcha = useCallback((): Promise<any> => {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();

      const check = () => {
        if (window.grecaptcha) {
          resolve(window.grecaptcha);
          return;
        }

        if (Date.now() - startTime > GRECAPTCHA_LOAD_TIMEOUT) {
          reject(new Error('Timeout aguardando reCAPTCHA carregar'));
          return;
        }

        setTimeout(check, 100);
      };

      check();
    });
  }, []);

  // Validar e gerar token
  const validate = useCallback(async (action: string = 'submit'): Promise<string> => {
    try {
      setError(null);

      // Preventiva 1: Evitar múltiplas validações simultâneas
      if (isValidatingRef.current) {
        const errorMsg = 'Validação de reCAPTCHA já em progresso';
        Apm.captureError(`reCAPTCHA: ${errorMsg}`);
        throw new Error(errorMsg);
      }

      isValidatingRef.current = true;

      // Preventiva 2: Verificar cooldown entre validações
      const now = Date.now();
      if (lastTokenTimeRef.current && now - lastTokenTimeRef.current < VALIDATION_COOLDOWN) {
        throw new Error('Aguarde antes de tentar novamente');
      }


      // Limpar token expirado
      if (token) {
        setToken(null);
        dispatch(SET_RECAPTCHA(undefined));
      }

      setIsLoading(true);

      // Preventiva 4: Aguardar grecaptcha estar disponível
      const grecaptcha = await waitForGrecaptcha();

      // Preventiva 5: Verificar se grecaptcha tem o método execute
      const executeMethod = getExecuteMethod(grecaptcha);

      // Limpar timeout anterior se existir
      if (tokenTimeoutRef.current) {
        clearTimeout(tokenTimeoutRef.current);
      }

      // Executar reCAPTCHA
      const siteKey = '6LcacoosAAAAAB7H7whlkWGX4GNXNwvesZHSypSe';
      if (!siteKey) {
        throw new Error('Site key do reCAPTCHA não configurada');
      }

      const newToken = await executeMethod.call(grecaptcha, siteKey, {
        action,
      });

      // Preventiva 6: Validar se token foi gerado corretamente
      if (!newToken || typeof newToken !== 'string') {
        throw new Error('Token do reCAPTCHA não foi gerado corretamente');
      }

      // Armazenar token e timestamp
      lastTokenTimeRef.current = now;
      setToken(newToken);
      dispatch(SET_RECAPTCHA(newToken));

      // Preventiva 7: Limpar token após expiração
      tokenTimeoutRef.current = setTimeout(() => {
        setToken(null);
        dispatch(SET_RECAPTCHA(undefined));
        lastTokenTimeRef.current = 0;
      }, TOKEN_EXPIRATION_TIME);

      return newToken;
    } catch (err: any) {
      const errorMsg = err?.message || 'Erro desconhecido ao validar reCAPTCHA';
      setError(errorMsg);

      // Tratar erro específico de DUPE
      if (errorMsg.includes('DUPE')) {
        Apm.captureError(`reCAPTCHA DUPE Error: ${errorMsg}`);
        throw new Error('Token do reCAPTCHA expirou, por favor tente novamente');
      }

      Apm.captureError(`reCAPTCHA Validation Error: ${errorMsg}`);
      throw err;
    } finally {
      isValidatingRef.current = false;
      setIsLoading(false);
    }
  }, [token, dispatch, waitForGrecaptcha]);

  // Reset do hook
  const reset = useCallback(() => {
    if (tokenTimeoutRef.current) {
      clearTimeout(tokenTimeoutRef.current);
      tokenTimeoutRef.current = null;
    }
    lastTokenTimeRef.current = 0;
    isValidatingRef.current = false;
    setToken(null);
    setError(null);
    dispatch(SET_RECAPTCHA(undefined));
  }, [dispatch]);

  return {
    validate,
    reset,
    token,
    isLoading,
    error,
  };
};

export default useRecaptcha;